import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import productsHeroImg from '../../assets/products_hero.png';
import getStaticFile from '../../utils/getStaticFile';
import styles from './Products.module.scss';
import { Button } from '@src/components/ui';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import Toast from '../../components/Toast';
import { formatCurrency } from '../../utils/formatters';
import { getProducts } from '../../api/ProductApi';
import { fetchProductCategoryTree } from '../../api/ProductCategoryApi';
import { requestError } from '../../utils/request';
import { useAuth } from '@src/contexts/AuthContext';
const sortCategories = (categories) => ([...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)));
const findCategoryBySlug = (categories, slug) => {
    for (const category of categories) {
        if (category.slug === slug) {
            return category;
        }
        if (category.children?.length) {
            const inChildren = findCategoryBySlug(category.children, slug);
            if (inChildren) {
                return inChildren;
            }
        }
    }
    return null;
};
const findCategoryPath = (categories, slug) => {
    for (const category of categories) {
        if (category.slug === slug) {
            return [category];
        }
        if (category.children?.length) {
            const childPath = findCategoryPath(category.children, slug);
            if (childPath) {
                return [category, ...childPath];
            }
        }
    }
    return null;
};
const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [toastMessage, setToastMessage] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [products, setProducts] = useState([]);
    const [categoryTree, setCategoryTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { addItem, isLoading: cartLoading } = useCart();
    const { isClinicAuthorized } = useAuth();
    const categoryFiltersRef = useRef(null);
    const selectedCategorySlug = searchParams.get('category') || '';
    const topLevelCategories = useMemo(() => sortCategories(categoryTree), [categoryTree]);
    const selectedCategory = useMemo(() => (selectedCategorySlug ? findCategoryBySlug(categoryTree, selectedCategorySlug) : null), [categoryTree, selectedCategorySlug]);
    const selectedCategoryPath = useMemo(() => (selectedCategorySlug ? findCategoryPath(categoryTree, selectedCategorySlug) || [] : []), [categoryTree, selectedCategorySlug]);
    const selectedRootCategory = selectedCategoryPath[0] || null;
    const visibleSubcategories = useMemo(() => sortCategories(selectedRootCategory?.children || []), [selectedRootCategory]);
    const blueSectionContent = useMemo(() => {
        if (!selectedCategoryPath.length)
            return null;
        const hasText = (v) => !!v?.trim();
        // Find the first category in the path with both headerTitle and headerDescription
        const resolvedCategory = [...selectedCategoryPath].reverse().find((cat) => hasText(cat.headerTitle) && hasText(cat.headerDescription));
        if (!resolvedCategory)
            return null;
        const titleSource = (resolvedCategory.headerTitle || '').trim();
        const descriptionSource = (resolvedCategory.headerDescription || '').trim();
        if (!titleSource || !descriptionSource)
            return null;
        // Find the first category in the path with a headerImageUrl
        const imageCategory = [...selectedCategoryPath].reverse().find((cat) => !!cat.headerImageUrl);
        return {
            title: `About ${titleSource}`,
            description: descriptionSource,
            imageUrl: imageCategory?.headerImageUrl || '',
        };
    }, [selectedCategoryPath]);
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const response = await fetchProductCategoryTree();
                const rawData = response.body;
                const treeData = Array.isArray(rawData) ? rawData : rawData.results || [];
                setCategoryTree(treeData);
            }
            catch (err) {
                console.error('Error loading categories:', err);
            }
        };
        loadCategories();
    }, []);
    useEffect(() => {
        loadProducts(selectedCategorySlug);
    }, [selectedCategorySlug]);
    useEffect(() => {
        if (selectedCategorySlug) {
            categoryFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedCategorySlug]);
    useEffect(() => {
        if (!loading && selectedCategorySlug) {
            categoryFiltersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [loading, selectedCategorySlug]);
    const loadProducts = async (categorySlug) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getProducts({
                is_available: true,
                category_slug: categorySlug || undefined,
            });
            setProducts(response.body);
        }
        catch (err) {
            console.error('Error loading products:', err);
            setError('Failed to load products. Please try again.');
            requestError(err, 'Failed to load products');
        }
        finally {
            setLoading(false);
        }
    };
    const handleCategoryClick = (categorySlug) => {
        if (!categorySlug) {
            setSearchParams({});
        }
        else {
            setSearchParams({ category: categorySlug });
        }
    };
    const handleAddToCart = async (product) => {
        if (cartLoading)
            return;
        try {
            await addItem({
                id: product.id.toString(),
                name: product.title,
                sku: product.sku,
                price: parseFloat(product.price),
                image: product.imageUrlDisplay || '',
                quantity: 1,
                notDiscountable: product.notDiscountable,
            });
            setToastMessage(`${product.title} added to cart`);
            setIsToastVisible(true);
        }
        catch (err) {
            console.error('Failed to add item to cart:', err);
            setToastMessage('Failed to add item to cart');
            setIsToastVisible(true);
        }
    };
    const renderProductTiles = (productRows, sectionTitle) => (_jsxs("section", { className: styles.productTiles, children: [_jsx("div", { className: styles.sectionHeader, children: _jsx("h2", { children: sectionTitle }) }), _jsx("div", { className: styles.tilesContainer, children: productRows.length === 0 ? (_jsx("div", { className: styles.noProducts, children: _jsx("p", { children: "No products available for this selection." }) })) : (productRows.map((product) => (_jsxs("div", { className: styles.productTile, children: [_jsxs(Link, { to: `/products/${product.path}`, children: [product.imageUrlDisplay && (_jsx("img", { src: product.imageUrlDisplay, alt: product.title })), _jsx("h3", { children: product.title }), _jsx("div", { className: styles.price, children: _jsx("span", { children: formatCurrency(isClinicAuthorized ? (product.displayPrice || product.price) : product.price) }) }), _jsx("p", { children: product.description }), !product.isAvailable && (_jsx("div", { className: styles.unavailableBadge, children: "Out of Stock" }))] }), isClinicAuthorized && (_jsx("div", { className: styles.buttonWrapper, children: _jsx(Button, { variant: "primary", label: cartLoading
                                    ? 'Adding...'
                                    : product.stockQuantity > 0
                                        ? 'Add to Cart'
                                        : 'Out of Stock', onClick: () => handleAddToCart(product), disabled: !product.isAvailable || product.stockQuantity === 0 || cartLoading }) }))] }, product.id)))) })] }));
    if (loading) {
        return (_jsx("main", { className: styles.products, children: _jsx("div", { className: styles.loadingContainer, children: _jsx("p", { children: "Loading products..." }) }) }));
    }
    if (error) {
        return (_jsx("main", { className: styles.products, children: _jsxs("div", { className: styles.errorContainer, children: [_jsx("p", { children: error }), _jsx(Button, { variant: "primary", label: "Retry", onClick: () => loadProducts(selectedCategorySlug) })] }) }));
    }
    return (_jsxs("main", { className: styles.products, children: [_jsx(Toast, { message: toastMessage, isVisible: isToastVisible, onClose: () => setIsToastVisible(false) }), _jsx("section", { className: styles.hero, children: (() => {
                    // For "All Products" (no category selected), use the old static fallback
                    if (!selectedCategorySlug) {
                        return (_jsxs(_Fragment, { children: [_jsx("img", { src: getStaticFile('assets/products_page_banner.png'), alt: "Product Details", className: styles.heroImage }), _jsx("div", { className: styles.heroContent, children: _jsxs("div", { children: [_jsx("h1", { children: "Innovative products advancing the standard of veterinary care." }), _jsx("p", {})] }) })] }));
                    }
                    // For specific categories, always use category name as title, and show description if present
                    const heroCategory = [...selectedCategoryPath].reverse().find((cat) => cat.name);
                    const heroImage = heroCategory?.headerImageUrl || productsHeroImg;
                    const heroTitle = heroCategory?.name || '';
                    const heroDescription = heroCategory?.description || '';
                    return (_jsxs(_Fragment, { children: [heroImage && (_jsx("img", { src: heroImage, alt: "Product Details", className: styles.heroImage })), _jsx("div", { className: styles.heroContent, children: _jsxs("div", { children: [_jsx("h1", { children: heroTitle }), heroDescription && _jsx("p", { children: heroDescription })] }) })] }));
                })() }), _jsx("section", { className: styles.categoryFilters, ref: categoryFiltersRef, children: _jsxs("div", { className: styles.categoryFiltersInner, children: [_jsx("button", { type: "button", className: `${styles.categoryPill} ${!selectedCategorySlug ? styles.categoryPillActive : ''}`, onClick: () => handleCategoryClick(''), children: "All Products" }), topLevelCategories.map((category) => (_jsx("button", { type: "button", className: `${styles.categoryPill} ${selectedRootCategory?.slug === category.slug ? styles.categoryPillActive : ''}`, onClick: () => handleCategoryClick(category.slug), children: category.name }, category.id)))] }) }), !!visibleSubcategories.length && (_jsx("section", { className: styles.subcategoryFilters, children: _jsxs("div", { className: styles.subcategoryFiltersInner, children: [_jsx("div", { className: styles.subcategoryHeading, children: _jsxs("h2", { children: [selectedRootCategory?.name, " Subcategories"] }) }), _jsxs("div", { className: styles.subcategoryPills, children: [_jsxs("button", { type: "button", className: `${styles.subcategoryPill} ${selectedCategorySlug === selectedRootCategory?.slug ? styles.subcategoryPillActive : ''}`, onClick: () => handleCategoryClick(selectedRootCategory?.slug || ''), children: ["All ", selectedRootCategory?.name] }), visibleSubcategories.map((category) => (_jsx("button", { type: "button", className: `${styles.subcategoryPill} ${selectedCategorySlug === category.slug ? styles.subcategoryPillActive : ''}`, onClick: () => handleCategoryClick(category.slug), children: category.name }, category.id)))] })] }) })), blueSectionContent && (_jsx("section", { className: styles.blueSection, children: _jsxs("div", { className: styles.blueContent, children: [_jsx("div", { className: styles.blueContent__title, children: _jsx("h2", { children: blueSectionContent.title }) }), _jsx("div", { className: styles.blueContent__subTitle, children: _jsx("p", { children: blueSectionContent.description }) }), !!blueSectionContent.imageUrl && (_jsx("img", { src: blueSectionContent.imageUrl, alt: `${selectedCategory?.name || 'Category'} header`, className: styles.blueBarImage }))] }) })), renderProductTiles(products, selectedCategory?.name ? `${selectedCategory.name} Products` : 'All Products')] }));
};
export default Products;
